
/**
 * 取当前选中的节点 id。
 *
 * 三级来源，任一级拿不到都不会崩 —— 最差退化成「不知道选中了谁」，
 * 而不是白屏。
 */
/**
 * 取消全部选中（节点 + 连线）。
 *
 * ⚠️ 必须走图引擎自己的 API，而不是自己遍历改 `node.selected`：
 * 只有它才会发出 `nodesChange`，从而触发宿主的**唯一写入口**把新文档
 * 发布给所有窗口。自己就地改在多窗口下会「只清掉当前窗口」。
 */
/**
 * Vue Flow 的 store 句柄。
 *
 * ⚠️ **用结构性类型，不 import `@vue-flow/core` 的类型。**
 * 那个包的类型极深（`Edge` / `Node` 的泛型一路嵌套），
 * 而这里 chỉ 用到三个方法 —— 引进来只会让每个调用点都被泛型缠住。
 */
/**
 * Vue Flow 的 store 句柄。
 *
 * ⚠️ **用结构性类型，不 import `@vue-flow/core` 的类型。**
 * 那个包的类型极深（`Edge` / `Node` 的泛型一路嵌套），而这里只用得到
 * 十来个方法 —— 引进来只会让每个调用点都被泛型缠住。
 *
 * ⚠️ 全部标成可选：不同小版本的 store 上方法并不齐（`getSelectedNodes`
 * 有的版本就没有），调用点用 `?.` 兜。**这不是偷懒** ——
 * 写成必选会让"某个版本没有这个方法"变成类型错误，
 * 而运行期我们本来就是靠 `unwrapArray` 兜底的。
 */
export interface FlowStoreApi {
  /* 读：形状在各版本间不一致，一律走 `unwrapArray` 解包 */
  getNodes?: unknown
  getEdges?: unknown
  getSelectedNodes?: unknown
  getSelectedEdges?: unknown
  getViewport?: unknown
  /*
   * 写 / 视图。
   *
   * ⚠️ `updateNodeInternals` **不是可选** —— 调用点是直接调的
   * （`store.updateNodeInternals()`，没有 `?.`）。写成可选会让每个
   * 调用点都报「可能是 undefined」，而那些调用点**本来就是安全的**
   * （外层已经有 `flowStore.value?.`）。
   *
   * ⚠️ 参数一律 `never[]`：Vue Flow 的小版本之间签名不一样
   * （`updateNodeInternals()` 有的版本不收参、`addSelectedNodes` 收的是
   * `{id}[]` 而不是 `string[]`）。**这不是偷懒** —— 猜一个具体签名，
   * 猜错就会在无关的调用点上报错，而这一层的职责只是"描述形状"。
   */
  updateNodeInternals: (...args: any[]) => unknown
  setViewport: (...args: any[]) => unknown
  fitView: (...args: any[]) => unknown
  zoomIn: (...args: any[]) => unknown
  zoomOut: (...args: any[]) => unknown
  setCenter: (...args: any[]) => unknown
  removeSelectedElements: (...args: any[]) => unknown
  addSelectedNodes: (...args: any[]) => unknown
  addNodes: (...args: any[]) => unknown
  addEdges: (...args: any[]) => unknown
  /* ⚠️ 这个的**返回值**是消费的（算坐标），所以签名要具体 */
  screenToFlowCoordinate: (p: { x: number; y: number }) => { x: number; y: number }
  findNode: (id: string) => unknown
  /** 从某个端口开始拉线（指针跟踪那套手势用） */
  startConnection: (...args: any[]) => unknown
  /**
   * 引擎那层框选正在进行中。
   *
   * ⚠️ 是个**可写 ref** —— 双指手势起来时要把它置 false 把框选撤掉
   * （见 `useCanvasPinch`）。可选：不同小版本不一定有。
   */
  userSelectionActive?: { value: boolean }
}

/**
 * 把上面那些「形状不一致」的值描述成一句人能读的话。
 *
 * ⚠️ 它是**诊断工具**，生产路径上没人调 —— 但
 * `verify:flow-integration` 用它钉住「各版本的 store 形状都兜住了」。
 * （我一度以为它是死代码，因为只 grep 了 `src/`；
 * **删代码前要 grep 整个仓库，包括 `scripts/`。**）
 */
export function describeShape(value: unknown): string {
  if (value === undefined) return 'undefined'
  if (value === null) return 'null'
  if (typeof value === 'function') return 'function'
  if (Array.isArray(value)) return `array(${value.length})`
  if (typeof value === 'object' && 'value' in (value as object)) {
    return `ref<${describeShape((value as { value: unknown }).value)}>`
  }
  return typeof value
}

/** 当前视口（平移 + 缩放）。拿不到时给一个"原点"兜底，调用方不用判空。 */
export function viewportOf(store: FlowStoreApi | null): { x: number; y: number; zoom: number } {
  const v = unwrapViewport(store?.getViewport)
  return v ?? { x: 0, y: 0, zoom: 1 }
}

/**
 * 视口的形状和解包那套一样会变（数组/ref/getter），单独解一次。
 * ⚠️ 和 `unwrapArray` 分开写：那个返回数组，这个返回对象 ——
 * 混在一起会让"拿到空数组当对象用"变成运行期 `undefined.x`。
 */
function unwrapViewport(value: unknown): { x: number; y: number; zoom: number } | null {
  const v = typeof value === 'function' ? (value as () => unknown)() : value
  const raw =
    v && typeof v === 'object' && 'value' in (v as object) ? (v as { value: unknown }).value : v
  if (!raw || typeof raw !== 'object') return null
  const { x, y, zoom } = raw as { x?: unknown; y?: unknown; zoom?: unknown }
  if (typeof x !== 'number' || typeof y !== 'number') return null
  return { x, y, zoom: typeof zoom === 'number' ? zoom : 1 }
}

/**
 * 把 store 上那些「可能是数组、也可能是 ref / getter」的东西解包成数组。
 *
 * ⚠️ Vue Flow 不同小版本的 `getSelectedNodes` 形状不一样：
 * 有的是数组、有的是 `Ref<数组>`、有的干脆是个函数。
 * 三种都兜住，最差返回空数组 —— 调用方不需要各写一遍。
 */
export function unwrapArray<T>(value: unknown): T[] {
  const v = typeof value === 'function' ? (value as () => unknown)() : value
  const raw = v && typeof v === 'object' && 'value' in (v as object) ? (v as { value: unknown }).value : v
  return Array.isArray(raw) ? (raw as T[]) : []
}

export function clearAllSelection(store: FlowStoreApi | null): void {
  store?.removeSelectedElements?.()
}

/**
 * **只选中这些**（替换式）—— 「产生了一个新对象，应该只剩它被选中」的统一入口。
 *
 * ## 它修的是什么
 *
 * `addSelectedNodes` 是**追加**语义，而加节点 / 粘贴 / 加资产这些操作
 * 想要的是**替换**。之前那几个调用点都只调了 `addSelectedNodes`，
 * 于是「选中 A → 加一个新节点」之后 **A 还亮着**，两个都在选中集里。
 * 后果不只是难看：多选态下拖动会**把 A 一起拖走**，而用户以为自己只是
 * 加了个节点。
 *
 * ## 为什么是"直接写标志"，而不是发一批 select 变更
 *
 * **选中的真源就是文档里那几个 `selected` 布尔**：我们的画布不持有自己的
 * 节点副本（`canvasState.getNodes()` 返回的就是宿主这一份，见那里的约束 ③），
 * 所以写标志 == 应用一批 select 变更，而且**所有窗口下一拍都会看到**。
 *
 * ⚠️ 反过来（发提案）有两个坑，都踩过：
 *   · 提案要么走 `removeSelectedElements()` + `addSelectedNodes()`（两批变更、
 *     两次发布，中间那一瞬间"什么都没选中"是真实的文档状态），
 *     要么走 store 的 `setSelection`；
 *   · 而**store 读的是组件的 props** —— 刚建出来的节点要等下一拍才进去，
 *     于是"加一个组件，它没被选中"（面板显示"没有选中元素"、把手不出来）。
 *     写标志没有这个时差。
 *
 * ⚠️ 多选手势（Shift 点选、框选累加、右键一个未选中的节点先把它选上）
 * 走的仍然是 `addSelectedNodes` —— 那才是"加"的语义。
 */
export function selectOnly(
  nodes: readonly { id: string; selected?: boolean }[] | undefined,
  ids: readonly string[],
): string[] {
  if (!nodes) return []
  const wanted = new Set(ids)
  const dirty: string[] = []
  for (const node of nodes) {
    const should = wanted.has(node.id)
    if (Boolean(node.selected) !== should) {
      node.selected = should
      dirty.push(node.id)
    }
  }
  /*
   * ⚠️ **返回被改动的 id，调用方必须拿去 `republish`**（`graph/documentSync.ts`）。
   *
   * 就地改字段没有任何"变更列表"可合并，而所有窗口 / 面板都是靠**引用变化**
   * 重新同步的 —— 不 republish 的话，写进去的选中**有时**看不到
   * （取决于那一刻有没有别的变更顺手换了数组）。
   * 这个"有时"在真机上表现为"点了没选中"，在验证里表现为"同一条断言
   * 单跑绿、连跑红"。`node.selected = false` 那条注释里也写了同一条规矩。
   */
  return dirty
}
export function selectedIdsOf(store: FlowStoreApi | null): string[] {
  if (!store) return []

  const direct = unwrapArray<{ id?: string }>(store.getSelectedNodes)
  if (direct.length > 0) return direct.map((n) => n.id ?? '').filter(Boolean)

  const inner = unwrapArray<{ id?: string; selected?: boolean }>(store.getNodes)
  if (inner.length > 0) {
    return inner.filter((n) => n.selected).map((n) => n.id ?? '').filter(Boolean)
  }

  return []
}

/** 取当前选中的连线 id */
export function selectedEdgeIdsOf(store: FlowStoreApi | null): string[] {
  return unwrapArray<{ id?: string }>(store?.getSelectedEdges)
    .map((e) => e.id ?? '')
    .filter(Boolean)
}

/** 取 store 里的全部节点（同样要解包） */
export function nodesOf(
  store: FlowStoreApi | null,
): { id: string; selected?: boolean }[] {
  return unwrapArray<{ id: string; selected?: boolean }>(store?.getNodes)
}
